import { CapacitorTwilioVoice } from "@capgo/capacitor-twilio-voice";
import { interfaceTranslator } from "@/i18n/translate";
import { promptToOpenAppSettings } from "@/lib/os-helpers";

/**
 * Ensure the microphone permission is granted before placing or
 * accepting a call.
 *
 * - If the OS already has it, returns `true` synchronously.
 * - If it hasn't been asked yet, fires the native prompt and returns
 *   the OS's verdict.
 * - On denial, surfaces a WhatsApp-style native alert (Cancel /
 *   Settings) via {@link promptToOpenAppSettings} and returns `false`.
 *   Tapping "Settings" deep-links to the app's OS settings page; on
 *   Android (no supported intent path today) it falls back to manual
 *   guidance.
 *
 * Used as a just-in-time gate inside Twilio's `makeCall` and
 * `acceptInvite`, so a brand-new user never sees the mic prompt
 * until they actively reach for a voice feature.
 */
export async function ensureMicGranted(): Promise<boolean> {
  const check = await CapacitorTwilioVoice.checkMicrophonePermission();
  if (check.granted) {
    return true;
  }
  const requested = await CapacitorTwilioVoice.requestMicrophonePermission();
  if (requested.granted) {
    return true;
  }
  const t = interfaceTranslator("communications.microphone");
  await promptToOpenAppSettings({
    message: t("permissionMessage"),
    settingsGuidance: t("settingsGuidance"),
    title: t("permissionTitle"),
  });
  return false;
}
